"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { TrendingUp, TrendingDown } from "lucide-react";

interface TickerItem {
  symbol: string;
  label: string;
  price: number;
  change: number;
  changePercent: number;
  currency: string;
  companySlug?: string;
  type: "index" | "stock";
}

function formatPrice(price: number, currency: string): string {
  if (currency === "USD") {
    return price >= 1000
      ? price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : price.toFixed(2);
  }
  return price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function TickerChip({ item }: { item: TickerItem }) {
  const isPositive = item.change >= 0;
  const sign = isPositive ? "+" : "";
  const colorClass = isPositive
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-red-600 dark:text-red-400";

  const inner = (
    <span className="inline-flex items-center gap-1.5 px-3 whitespace-nowrap select-none">
      <span className="text-xs font-semibold text-foreground/60 uppercase tracking-wide">
        {item.symbol}
      </span>
      <span className="text-xs font-medium text-foreground">
        {formatPrice(item.price, item.currency)}
      </span>
      <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${colorClass}`}>
        {isPositive ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        {sign}{item.changePercent.toFixed(2)}%
      </span>
      <span className="text-foreground/20 ml-1">·</span>
    </span>
  );

  if (item.companySlug) {
    return (
      <Link
        href={`/companies/${item.companySlug}`}
        className="hover:bg-foreground/10 rounded transition-colors"
        prefetch={false}
      >
        {inner}
      </Link>
    );
  }

  return <span>{inner}</span>;
}

export function MarketTickerBar() {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/market-ticker")
      .then((r) => r.json())
      .then((data: TickerItem[]) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setItems(data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (!ready || items.length === 0) {
    return (
      <div className="h-8 bg-muted border-b border-border" aria-hidden={!ready} />
    );
  }

  // Duplicate items so the seamless loop works
  const doubled = [...items, ...items];

  return (
    <div
      className="bg-muted border-b border-border overflow-hidden h-8 flex items-center"
      aria-label="Market ticker"
    >
      <div
        className="flex items-center animate-ticker"
        style={{ willChange: "transform" }}
      >
        {doubled.map((item, i) => (
          <TickerChip key={`${item.symbol}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}
